import {
  ApeironLinkButton,
  ApeironLoopVideo,
  INFO_POPUP_TYPE,
  InfoPopup,
  noForwardProps
} from '@apeiron/temp'
import { AnimateTime } from '@apeiron/ui'
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay'
import { styled, Typography } from '@mui/material'
import ENV_CONFIG from '@src/config'
import { SkillPlaybackType } from '@src/constants/skill'
import {
  FC,
  MouseEvent,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState
} from 'react'

const Icon = styled(SmartDisplayIcon)`
  color: white;
  font-size: 28px;
`

const Popup = styled(InfoPopup)`
  background-color: #22334b;
  background: linear-gradient(180deg, #263852 0%, #0f1f36 50%);
  border-radius: 6px;
  max-height: 95vh;
  max-width: 650px;
  width: 95vw;

  .Custom-Content {
    align-items: center;
    display: flex;
    flex-direction: column;
    width: 100%;

    ${props => props.theme.breakpoints.up('res480')} {
      gap: 10px;
      padding: 0 40px 40px 40px;
    }
    ${props => props.theme.breakpoints.down('res480')} {
      gap: 12px;
      padding: 0 20px 20px 20px;
    }
  }
`

const Name = styled(Typography)`
  color: #cccccc;
  font-weight: 400;
  line-height: 1;

  ${props => props.theme.breakpoints.up('res480')} {
    font-size: 20px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    font-size: 18px;
  }
`

const Video = styled(ApeironLoopVideo, noForwardProps)<VideoProps>`
  aspect-ratio: 16 / 9;
  background-color: #ffffff11;
  border-radius: 4px;
  border: 2px solid #c7af8f;
  padding: 3px;
  width: 100%;

  transition: filter ${AnimateTime}ms, transform ${AnimateTime / 2}ms;
  ${props =>
    props.__fadeIn
      ? `
        filter: brightness(1.05) grayScale(0) saturate(1.05) contrast(1.05);
        transform: scaleY(1);
      `
      : `
        filter: brightness(5) grayScale(1);
        transform: scaleY(0);
      `}
`

const SkillPlayback: FC<Props> = props => {
  const { className, name, skill, type } = props

  const [open, setOpen] = useState<boolean>(false)

  const [play, setPlay] = useState<boolean>(false)

  const path = useMemo(() => {
    return `${ENV_CONFIG.DOMAIN_URL.SKILL_VIDEO}videos/skills/${type}/${skill}.mp4`
  }, [skill, type])

  const handleOnOpen = useCallback(() => {
    setOpen(true)
  }, [])

  const handleOnStart = useCallback(() => {
    setPlay(true)
  }, [])

  const handleOnEnd = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setPlay(false)
    setOpen(false)
    event.stopPropagation()
  }, [])

  const handleOnVideoEnd = useCallback((event: SyntheticEvent) => {
    const video = event.target as HTMLVideoElement

    video.currentTime = 0
    video.play()
  }, [])

  return (
    <>
      <ApeironLinkButton className={className} onClick={handleOnOpen}>
        <Icon className='Custom-Icon' />
      </ApeironLinkButton>
      <Popup type={INFO_POPUP_TYPE.BLACK} open={open} onClose={handleOnEnd}>
        <Name>{name}</Name>
        <Video
          __fadeIn={play}
          id='aperion'
          autoPlay
          playbackRate={1.5}
          src={path}
          onEnded={handleOnVideoEnd}
          onLoadedData={handleOnStart}
        />
      </Popup>
    </>
  )
}

type VideoProps = {
  __fadeIn?: boolean
}

type Props = {
  className?: string
  name: string
  skill: string
  type: SkillPlaybackType
}

export default SkillPlayback
